<template>
    <div>
        <div class="dx-field">
            <div class="dx-field-label">
                {{ meta.title }}
                <div v-if="isRequiered" class="dx-field-item-required-mark">&nbsp;*</div>
            </div>
            <div :class="fieldValueClass">
                <dx-tag-box
                    ref="tagBoxRef"
                    v-model:value="value"
                    :disabled="disabled"
                    :display-expr="meta.extParams.displayExpr"
                    :items="dataSource"
                    :max-displayed-tags="2"
                    :show-selection-controls="true"
                    :value-expr="meta.extParams.valueExpr"
                    @opened="onOpenHandler"
                    @valueChanged="valueChanged"
                />
                <div
                    v-if="error"
                    class="ai-dx-invalid"
                    @click="() => callbacks.notifyErrorMessage(error.message)"
                ></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onBeforeMount, ref } from 'vue';
import { DxTagBox } from 'devextreme-vue';
import { getDictionary } from '@/service/card-service';

const props = defineProps({
    ds: {
        type: Array,
        default: () => [],
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    callbacks: {
        type: Object,
        default: () => ({
            userDataChanged: null,
            getDictionary: null,
        }),
    },
    meta: {
        type: Object,
        default: () => {},
    },
});

const tagBoxRef = ref();
const serverData = inject('serverData');
const userData = inject('userData');
const dataSource = ref(props.ds);
const validationResult = inject('validationResult');

const isRequiered = computed(() => {
    return props.meta.validationRules?.required;
});

const error = computed(() => {
    return validationResult.value.find(s => s.fieldNameText === props.meta.fieldNameId);
});

const onOpenHandler = async () => {
    await getDictionaryHandler();
};

const getDictionaryHandler = async () => {
    if (props.callbacks.getDictionary) {
        await props.callbacks.getDictionary();
    } else {
        const response = await getDictionary(props.meta.extParams.dictionaryRoutePath, {rightName: props.meta.extParams.dictionaryRoutePath});
        if (response) {
            dataSource.value = [...response];
        } else {
            console.log(response.error, response.uuid);
        }
    }
};

const valueChanged = e => {
    validationResult.value = validationResult.value.filter(s => {
        return s.fieldNameText !== props.meta.fieldNameId;
    });
    userDataChange(e.value);
};

const isModified = computed(() => {
    const userArray = userData?.value?.[props.meta.fieldNameId] ?? [];
    const serverArray = serverData?.value?.[props.meta.fieldNameId] ?? [];
    if (userArray.length !== serverArray.length) {
        return true;
    } else {
        return !userArray.every(value => serverArray.includes(value));
    }
});

const userDataChange = value => {
    if (dataSource.value && dataSource.value.length !== 0) {
        userData.value[props.meta.fieldNameId] = value;
        userData.value[props.meta.fieldNameText] = value.map(value => {
            const dataRow = dataSource.value.find(item => item[props.meta.extParams.valueExpr] === value);
            return dataRow[props.meta.extParams.displayExpr];
        });
        props.callbacks.userDataChanged();
    }
};

const value = computed(() => {
    if (userData.value[props.meta.fieldNameId] === undefined) {
        return [];
    }
    return userData.value[props.meta.fieldNameId];
});

onBeforeMount(async () => {
    const keys = userData?.value[props.meta.fieldNameId];
    const values = userData?.value[props.meta.fieldNameText];

    const initialDS = keys?.map((key, index) => ({
        [props.meta.extParams.valueExpr]: key,
        [props.meta.extParams.displayExpr]: values[index],
    }));
    if (dataSource.value.length === 0 && serverData?.value.length !== 0 && initialDS) {
        dataSource.value = initialDS;
    }
});

const fieldValueClass = computed(() => {
    return {
        'dx-field-value': true,
        'ai-field-modified': isModified.value,
    };
});
defineExpose({
    tagBoxRef,
});
</script>
